/**
 * navi-speech.js — NAVI's voice.
 *
 * Two engines, one API:
 *   1. **OpenAI TTS** (preferred) — high-quality, warm female voice
 *      (`coral`) synthesised on the backend at `/api/navi/speak`. Audio
 *      is delivered as MP3 bytes and cached both server-side (hashed
 *      by text + voice + language) and client-side (Object URLs keyed
 *      on the same tuple) so repeated playback is instant.
 *   2. **Browser Web Speech** — offline fallback that kicks in when
 *      the backend TTS is unreachable or the audio element fails to
 *      play. Loud speech should never *silently* fail.
 *
 * The consumer keeps global control: a persistent mute flag
 * (`altara.wallet.navi.tts_muted`) silences both engines, and every
 * NAVI message has a manual speaker button.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/** App language → BCP-47 tags for Web Speech fallback. */
const LANG_TO_BCP47 = {
  en:  ["en-ZA", "en-GB", "en-US", "en"],
  af:  ["af-ZA", "af", "nl-NL", "nl", "en-ZA"],
  zu:  ["zu-ZA", "zu", "en-ZA"],
  xh:  ["xh-ZA", "xh", "en-ZA"],
  st:  ["st-ZA", "st", "en-ZA"],
  tn:  ["tn-ZA", "tn", "en-ZA"],
  nso: ["nso-ZA", "nso", "en-ZA"],
  ts:  ["ts-ZA", "ts", "en-ZA"],
  ss:  ["ss-ZA", "ss", "en-ZA"],
  ve:  ["ve-ZA", "ve", "en-ZA"],
  nr:  ["nr-ZA", "nr", "en-ZA"],
};

export function isWebSpeechSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

function pickBrowserVoice(language) {
  if (!isWebSpeechSupported()) return null;
  const wanted = LANG_TO_BCP47[language] || LANG_TO_BCP47.en;
  const voices = window.speechSynthesis.getVoices() || [];
  if (voices.length === 0) return null;
  for (const tag of wanted) {
    const exact = voices.find(v => v.lang?.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;
    const prefix = voices.find(v => v.lang?.toLowerCase().startsWith(tag.split("-")[0].toLowerCase()));
    if (prefix) return prefix;
  }
  return voices[0];
}

const MUTE_KEY = "altara.wallet.navi.tts_muted";

/** Module-level Object-URL cache so repeated plays never re-download. */
const audioUrlCache = new Map();  // key: `${lang}|${text}` → objectURL

async function fetchOpenAIAudioUrl(text, language) {
  const key = `${language}|${text}`;
  if (audioUrlCache.has(key)) return audioUrlCache.get(key);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12000);
  try {
    const resp = await fetch(`${API}/navi/speak`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, language }),
      signal: controller.signal,
    });
    if (!resp.ok) throw new Error(`TTS HTTP ${resp.status}`);
    const blob = await resp.blob();
    if (!blob || blob.size === 0) throw new Error("Empty audio blob");
    const url = URL.createObjectURL(blob);
    audioUrlCache.set(key, url);
    return url;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * useNaviSpeech — React hook exposing NAVI's voice + mute controls.
 * Returns { supported, muted, toggleMute, speak, stop, speakingKey,
 * engine, voicesReady }.
 *
 * `speak(text, key, {force, onEnd})`
 *   - Cancels any prior utterance.
 *   - Attempts OpenAI TTS via the backend; falls back to Web Speech
 *     on failure so speech never silently drops.
 *   - Respects the global mute unless `force: true`.
 */
export function useNaviSpeech(language = "en") {
  const [muted, setMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(MUTE_KEY) === "1";
  });
  const [speakingKey, setSpeakingKey] = useState(null);
  const [voicesReady, setVoicesReady] = useState(false);
  const [engine, setEngine] = useState("openai");   // "openai" | "webspeech" | "none"
  const audioRef = useRef(null);
  const currentKey = useRef(null);

  useEffect(() => {
    if (!isWebSpeechSupported()) return;
    const onVoices = () => setVoicesReady(true);
    window.speechSynthesis.onvoiceschanged = onVoices;
    if ((window.speechSynthesis.getVoices() || []).length > 0) setVoicesReady(true);
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* ignore */ }
      audioRef.current = null;
    }
    if (isWebSpeechSupported()) {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    }
    currentKey.current = null;
    setSpeakingKey(null);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(m => {
      const next = !m;
      try { window.localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      if (next) stop();
      return next;
    });
  }, [stop]);

  const speakViaWebSpeech = useCallback((text, key, onEnd) => {
    if (!isWebSpeechSupported()) { onEnd?.(); return; }
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    const voice = pickBrowserVoice(language);
    if (voice) { utt.voice = voice; utt.lang = voice.lang; }
    utt.rate = 1.0;
    utt.pitch = 1.05;
    const done = () => {
      if (currentKey.current === key) setSpeakingKey(null);
      currentKey.current = null;
      onEnd?.();
    };
    utt.onend = done;
    utt.onerror = done;
    setEngine("webspeech");
    setSpeakingKey(key);
    currentKey.current = key;
    window.speechSynthesis.speak(utt);
  }, [language]);

  const speak = useCallback(async (text, key = null, { force = false, onEnd } = {}) => {
    if (!text) return;
    if (muted && !force) return;
    stop();
    const localKey = key || `spk_${Date.now()}`;
    currentKey.current = localKey;
    setSpeakingKey(localKey);

    try {
      const url = await fetchOpenAIAudioUrl(text, language);
      if (currentKey.current !== localKey) return;  // superseded while fetching
      const audio = new Audio(url);
      audioRef.current = audio;
      const done = () => {
        if (currentKey.current === localKey) setSpeakingKey(null);
        currentKey.current = null;
        audioRef.current = null;
        onEnd?.();
      };
      audio.onended = done;
      audio.onerror = () => {
        // Fall back to Web Speech if the audio element chokes
        audioRef.current = null;
        speakViaWebSpeech(text, localKey, onEnd);
      };
      setEngine("openai");
      await audio.play();
    } catch (e) {
      // OpenAI TTS unreachable → fall back cleanly
      speakViaWebSpeech(text, localKey, onEnd);
    }
  }, [language, muted, stop, speakViaWebSpeech]);

  return useMemo(() => ({
    supported: true,   // at least one engine is always tried
    voicesReady,
    engine,
    muted, toggleMute,
    speak, stop,
    speakingKey,
  }), [engine, muted, toggleMute, speak, stop, speakingKey, voicesReady]);
}
